const PREVIEW_TRANSITION_MS = 320;

class SolsticeProductCard extends HTMLElement {
  connectedCallback() {
    if (this.dataset.previewReady === 'true') return;
    this.mainImage = this.querySelector('.solstice__product-card-image:not(.solstice__product-card-image--preview)');
    this.previewImages = [...this.querySelectorAll('.solstice__product-card-image--preview')];
    this.swatches = [...this.querySelectorAll('.solstice__product-card-swatches__link[data-preview-image]')];
    this.cardLink = this.querySelector('.solstice__product-card-link');
    this.priceContainer = this.querySelector('.solstice__product-card-price');
    this.badgeContainer = this.querySelector('.solstice__product-card-badges');

    if (!this.mainImage || !this.cardLink || this.previewImages.length < 2) return;

    this.dataset.previewReady = 'true';
    this.previewToken = 0;
    this.activePreview = null;
    this.cleanupTimer = null;
    this.prefetched = false;
    this.isTouchMode = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    this.activeSwatch = null;
    this.touchSwatchArmed = null;
    this.pendingTouchSwatch = null;
    this.imageHovered = false;
    this.originalPrice = this.priceContainer?.innerHTML;
    this.originalBadge = this.badgeContainer?.innerHTML;
    this.originalHref = this.cardLink.getAttribute('href');

    this.swatches.forEach((swatch) => {
      if (!this.isTouchMode) {
        swatch.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch' && !this.isTouchMode) this.showPreview(swatch);
        });
        swatch.addEventListener('focus', () => {
          if (!this.isTouchMode) this.showPreview(swatch);
        });
      }
      swatch.addEventListener('click', (event) => this.handleSwatchClick(event, swatch));
    });

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || this.isTouchMode) return;
      this.isTouchMode = true;
      this.initializeTouchState();
    }, { capture: true });

    if (this.isTouchMode) {
      this.initializeTouchState();
    } else {
      this.addEventListener('pointerenter', () => this.prefetchPreviews(), { once: true });
      this.addEventListener('pointerleave', () => {
        if (!this.isTouchMode) this.resetPreview();
      });
      this.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch') return;
        const bounds = this.mainImage.getBoundingClientRect();
        const overImage = event.clientX >= bounds.left && event.clientX <= bounds.right
          && event.clientY >= bounds.top && event.clientY <= bounds.bottom
          && !event.target.closest('.solstice__product-card-swatches__link');
        this.setImageHover(overImage);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.isTouchMode && !this.contains(event.relatedTarget)) this.resetPreview();
      });
    }
  }

  getPreviewSrc(swatch) {
    if (this.isTouchMode) return swatch.dataset.previewImageMobile || swatch.dataset.previewImage;
    return swatch.dataset.previewImage;
  }

  initializeTouchState() {
    const cardUrl = new URL(this.cardLink.href, window.location.href).href;
    const selectedSwatch = this.swatches.find((swatch) => new URL(swatch.href, window.location.href).href === cardUrl);
    // An Algolia-matched variant can sit outside the four ranked previews.
    // Leave its Shopify-rendered image, price and link intact in that case.
    if (!selectedSwatch) return;
    this.selectSwatch(selectedSwatch);

    if (!this.isSameMedia(this.mainImage.currentSrc || this.mainImage.src, this.getPreviewSrc(selectedSwatch))) {
      this.showPreview(selectedSwatch);
    }
  }

  isSameMedia(firstSrc, secondSrc) {
    const firstUrl = new URL(firstSrc, window.location.href);
    const secondUrl = new URL(secondSrc, window.location.href);
    return firstUrl.origin === secondUrl.origin && firstUrl.pathname === secondUrl.pathname;
  }

  handleSwatchClick(event, swatch) {
    if (event.pointerType === 'touch' && !this.isTouchMode) {
      this.isTouchMode = true;
      this.initializeTouchState();
    }
    if (!this.isTouchMode) return;

    // The initial variant is already selected when the card loads, but it has
    // not received a customer tap. Let the first touch confirm any swatch.
    if (swatch === this.touchSwatchArmed && swatch === this.activeSwatch) return;

    event.preventDefault();
    event.stopPropagation();
    this.touchSwatchArmed = swatch;
    if (swatch !== this.activeSwatch && swatch !== this.pendingTouchSwatch) {
      this.pendingTouchSwatch = swatch;
      this.showPreview(swatch).finally(() => {
        if (this.pendingTouchSwatch === swatch) this.pendingTouchSwatch = null;
      });
    }
  }

  selectSwatch(selectedSwatch) {
    this.activeSwatch = selectedSwatch;

    this.swatches.forEach((swatch) => {
      if (swatch === selectedSwatch) {
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.removeAttribute('aria-current');
      }
    });

    this.cardLink.setAttribute('href', selectedSwatch.getAttribute('href'));
    const price = selectedSwatch.parentElement.querySelector('template[data-swatch-price]');
    if (price && this.priceContainer) {
      this.priceContainer.replaceChildren(price.content.cloneNode(true));
    }
    const badges = selectedSwatch.parentElement.querySelector('template[data-swatch-badges]');
    if (badges && this.badgeContainer) {
      this.badgeContainer.replaceChildren(badges.content.cloneNode(true));
    }
  }
  prefetchPreviews() {
    if (this.prefetched) return;
    this.prefetched = true;

    this.swatches.forEach((swatch) => {
      const image = new Image();
      image.src = this.getPreviewSrc(swatch);
    });
  }

  setImageHover(hovered) {
    if (this.isTouchMode || this.imageHovered === hovered) return;
    this.imageHovered = hovered;
    const src = hovered
      ? (this.activeSwatch?.dataset.hoverImage || this.dataset.hoverImage)
      : (this.activeSwatch ? this.getPreviewSrc(this.activeSwatch) : this.mainImage.currentSrc || this.mainImage.src);
    this.showImage(src);
  }

  async showPreview(swatch) {
    this.imageHovered = false;
    return this.showImage(this.getPreviewSrc(swatch), () => this.selectSwatch(swatch));
  }

  async showImage(src, onReady = () => {}) {
    if (!src) return;
    const token = ++this.previewToken;
    if (this.activePreview?.dataset.currentSrc === src) {
      onReady();
      return;
    }
    const image = new Image();
    image.src = src;

    try {
      await image.decode();
    } catch {
      // Keep the existing image, price and link together if the image failed.
      if (!image.complete || !image.naturalWidth) return;
    }

    if (token !== this.previewToken) return;
    onReady();

    window.clearTimeout(this.cleanupTimer);

    const previousPreview = this.activePreview;
    const nextPreview = this.previewImages.find((preview) => preview !== previousPreview);

    nextPreview.classList.remove('is-visible');
    nextPreview.src = src;
    nextPreview.dataset.currentSrc = src;
    nextPreview.style.zIndex = '2';

    if (previousPreview) previousPreview.style.zIndex = '1';

    // Commit the hidden state before fading this layer over the current image.
    nextPreview.getBoundingClientRect();
    nextPreview.classList.add('is-visible');
    this.activePreview = nextPreview;

    if (previousPreview) {
      this.cleanupTimer = window.setTimeout(() => {
        if (this.activePreview !== nextPreview) return;
        previousPreview.classList.remove('is-visible');
        previousPreview.removeAttribute('data-current-src');
        previousPreview.style.removeProperty('z-index');
      }, PREVIEW_TRANSITION_MS);
    }
  }

  resetPreview() {
    this.imageHovered = false;
    this.previewToken += 1;
    window.clearTimeout(this.cleanupTimer);

    this.previewImages.forEach((preview) => {
      preview.classList.remove('is-visible');
      preview.removeAttribute('data-current-src');
      preview.style.removeProperty('z-index');
    });

    this.activePreview = null;
    this.activeSwatch = null;
    this.cardLink.setAttribute('href', this.originalHref);
    if (this.priceContainer) this.priceContainer.innerHTML = this.originalPrice;
    if (this.badgeContainer) this.badgeContainer.innerHTML = this.originalBadge;
    this.swatches.forEach((swatch) => swatch.removeAttribute('aria-current'));
  }
}

if (!customElements.get('solstice-product-card')) {
  customElements.define('solstice-product-card', SolsticeProductCard);
}
